/* eslint-disable react/jsx-props-no-spreading */
import { DeleteOutlined } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format, startOfDay } from 'date-fns';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';

import { copy, paste } from '../../api/ventura/contract';
import { API_S3_UPLOAD_INFO_URL } from '../../constants/apiUrls';
import { contractFormText } from '../../constants/contracts';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { useSnackBar } from '../../hooks/useSnackBar';
import { isBlank, omitCents, round } from '../../util/helpers';
import { EMPTY_KPI_OPTIONS, KpiOptions, normalizeKpiOptionsResponse } from '../../util/kpiOptions';
import mapContractToFormUtil from '../../util/mapContractToForm';
import mapFormToContractUtil from '../../util/mapFormToContract';
import mapFormToLineItemUtil from '../../util/mapFormToLineItem';
import mapLineItemToForm from '../../util/mapLineItemToForm';
import { camelCaseKeys } from '../../util/object';
import posthog from '../../util/posthog';
import { getRequestJSON } from '../../util/services';
import DatePicker from '../forms/DatePicker';
import FormEndOptions from '../forms/FormEndOptions';
import NumericTextField from '../forms/NumericTextField';
import S3Upload from '../forms/S3Upload';
import TextField from '../forms/TextField';
import DuplicateLineItemContainer from '../LineItems/DuplicateLineItemContainer';

interface DuplicateContractFormProps {
  contract: any;
  onClose?: Function;
}

const mapContractToForm = (response) => {
  // Use mapContractToFormUtil so we use the same mapping functions
  // as the contract form.
  const contract = mapContractToFormUtil(response);
  return {
    name: contract.contractName,
    start_date: contract.startDate,
    end_date: contract.endDate,
    advisory_budget: contract.advisoryBudget,
    bonus: contract.bonus,
    contract_kpis: contract.contractKpis || [],
    insertion_orders: Object.entries(response.insertion_orders).map(([lineItemId, lineItem]) => ({
      ...(lineItem as object),
      clone: {
        ...(lineItem as any).clone,
        campaigns: Object.entries((lineItem as any).clone.campaigns).map(([adGroupId, adGroup]) => ({
          ...(adGroup as object),
          adGroupId,
        })),
        copy: true,
      },
      insertionOrderId: lineItemId,
    })),
  };
};

const mapFormToContract: any = (form) => {
  const newContract = mapFormToContractUtil(camelCaseKeys(form.contract));
  return {
    id: form.id,
    number_of_copies: form.number_of_copies,
    contract: {
      name: form.contract.name,
      start_date: form.contract.start_date,
      end_date: form.contract.end_date,
      advisory_budget: newContract.advisory_budget,
      bonus: newContract.bonus,
      io_path: form.contract.io_path,
      insertion_orders: undefined,
      contract_kpis_attributes: form.contract.contract_kpis?.map((kpi, index) => ({
        kpi_name: kpi.kpiName,
        provider: kpi.provider || null,
        goal_value: typeof kpi.goalValue === 'string' ? parseFloat(kpi.goalValue) : kpi.goalValue,
        goal_format: kpi.goalFormat,
        position: index,
        // eslint-disable-next-line no-underscore-dangle
        _destroy: kpi._destroy || false,
      // eslint-disable-next-line no-underscore-dangle
      })).filter(kpi => !kpi._destroy) || [],
    },
    insertion_orders: form.insertion_orders.reduce((accumulatorA, currentValueA) => {
      if (currentValueA.clone.copy) {
        const newLineItem = mapFormToLineItemUtil(currentValueA.clone);
        // eslint-disable-next-line no-param-reassign
        accumulatorA[currentValueA.insertionOrderId] = {
          ...currentValueA.clone,
          advisory_budget: newLineItem.advisory_budget,
          bonus: Number(newLineItem.bonus),
          campaigns: currentValueA.clone.campaigns.reduce((accumulatorB, currentValueB) => {
            // eslint-disable-next-line no-param-reassign
            accumulatorB[currentValueB.adGroupId] = {
              ...currentValueB,
              adGroupId: undefined,
            };
            return accumulatorB;
          }, {}),
          copy: undefined,
          customDeliveryProfiles: undefined,
          copyDeliveryProfiles: undefined,
          deletedProfileIds: undefined,
        };
      }
      return accumulatorA;
    }, {}),
  };
};

// Simplified KPI Section Component
function DuplicateKpiSection({ 
  control, 
  watch, 
  setValue, 
  apiHost, 
  hideSectionTitle = false,
  hideKpiCount = false,
}: { 
  control: any; 
  watch: any; 
  setValue: any; 
  apiHost: string; 
  hideSectionTitle?: boolean;
  hideKpiCount?: boolean;
}) {
  const [kpiOptions, setKpiOptions] = useState<KpiOptions>(EMPTY_KPI_OPTIONS);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize lists to cut rerenders while typing
  const kpiList = React.useMemo(() => Array.isArray(kpiOptions?.kpis) ? kpiOptions.kpis : [], [kpiOptions]);
  const providerList = React.useMemo(() => Array.isArray(kpiOptions?.providers) ? kpiOptions.providers : [], [kpiOptions]);

  const {
    fields: kpiFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'contract.contract_kpis',
  });

  useEffect(() => {
    const currentKpis = watch('contract.contract_kpis') || [];
    // eslint-disable-next-line no-underscore-dangle
    const activeKpis = currentKpis.filter((kpi: any) => !kpi._destroy);
    if (activeKpis.length === 0) {
      append({
        kpiName: '',
        provider: '',
        goalValue: '',
        goalFormat: 'number',
        position: 0
      }, { shouldFocus: true });
    }
  }, [append, watch]);

  useEffect(() => {
    const fetchKpiOptions = async () => {
      try {
        setIsLoading(true);

        // Single source of truth – contractId is irrelevant
        const resp = await getRequestJSON(apiHost, '/admin/contracts/kpi_options');
        setKpiOptions(normalizeKpiOptionsResponse(resp));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching KPI options:', error);
        setKpiOptions(EMPTY_KPI_OPTIONS);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKpiOptions();
  }, [apiHost]);

  const handleAddKpi = () => {
    append({ 
      kpiName: '', 
      provider: '', 
      goalValue: '', 
      goalFormat: 'number',
      position: kpiFields.length
    }, { shouldFocus: true });
  };

  // Watch for KPI name changes to auto-set goal format and default values
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && name.startsWith('contract.contract_kpis.') && name.endsWith('.kpiName')) {
        const index = parseInt(name.split('.')[2], 10);
        const kpiName = value.contract?.contract_kpis?.[index]?.kpiName;

        const formatMap = kpiOptions.kpi_goal_formats || {};
        const defaultMap = kpiOptions.kpi_default_values || {};

        if (kpiName && formatMap[kpiName]) {
          setValue(
            `contract.contract_kpis.${index}.goalFormat`,
            formatMap[kpiName],
            { shouldDirty: true, shouldValidate: true }
          );

          const defaultValue = defaultMap[kpiName];
          if (defaultValue !== undefined) {
            const currentGoalValue = value.contract?.contract_kpis?.[index]?.goalValue;
            if (currentGoalValue === '' || currentGoalValue == null) {
              setValue(
                `contract.contract_kpis.${index}.goalValue`,
                defaultValue.toString(),
                { shouldDirty: true, shouldValidate: true }
              );
            }
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, kpiOptions]);

  const getGoalLabel = (index: number) => {
    const goalFormat = watch(`contract.contract_kpis.${index}.goalFormat`);
    if (goalFormat === 'percent') return `${contractFormText.kpiSection.goalLabel} (%)`;
    if (goalFormat === 'dollars') return `${contractFormText.kpiSection.goalLabel} ($)`;
    return contractFormText.kpiSection.goalLabel;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
      {!hideSectionTitle && (
        <Typography variant="h6" mb={2}>
          {contractFormText.sectionTitles.kpiSettings}
        </Typography>
      )}
      <Stack spacing={2}>
        {!hideKpiCount && (
          <Stack direction="row" width={1} justifyContent="flex-start" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.primary">
              KPIs ({kpiFields.length})
            </Typography>
          </Stack>
        )}

        <Stack spacing={2}>
          {kpiFields
            .map((field, index) => ({ field, index }))
            .filter(({ index }) => !watch(`contract.contract_kpis.${index}._destroy`))
            .map(({ field, index }) => (
              <Grid container spacing={2} key={field.id} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`contract.contract_kpis.${index}.kpiName`}
                    control={control}
                    rules={{ required: 'KPI is required' }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Autocomplete
                        value={value || null}
                        onChange={(_, v) => onChange((v || '').trim())}
                        options={kpiList}
                        disabled={isLoading}
                        freeSolo={false}
                        loading={isLoading}
                        openOnFocus
                        autoHighlight
                        getOptionLabel={(o) => (typeof o === 'string' ? o : '')}
                        isOptionEqualToValue={(o, v) => o === v}
                        renderInput={(params) => {
                          const { InputLabelProps, InputProps, ...other } = params;
                          return (
                            <MuiTextField
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...other}
                              InputLabelProps={InputLabelProps}
                              InputProps={InputProps}
                              fullWidth
                              variant="outlined"
                              label={`${contractFormText.kpiSection.kpiLabel} *`}
                              error={!!error}
                              helperText={error?.message}
                            />
                          );
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3.5}>
                  <Controller
                    name={`contract.contract_kpis.${index}.provider`}
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Autocomplete
                        value={value || null}
                        onChange={(_, v) => onChange((v || '').trim())}
                        options={providerList}
                        disabled={isLoading}
                        freeSolo={false}
                        loading={isLoading}
                        openOnFocus
                        autoHighlight
                        getOptionLabel={(o) => (typeof o === 'string' ? o : '')}
                        isOptionEqualToValue={(o, v) => o === v}
                        renderInput={(params) => {
                          const { InputLabelProps, InputProps, ...other } = params;
                          return (
                            <MuiTextField
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...other}
                              InputLabelProps={InputLabelProps}
                              InputProps={InputProps}
                              fullWidth
                              variant="outlined"
                              label={contractFormText.kpiSection.providerLabel}
                              error={!!error}
                              helperText={error?.message}
                            />
                          );
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3.5}>
                  <Controller
                    name={`contract.contract_kpis.${index}.goalValue`}
                    control={control}
                    rules={{ 
                      required: { value: true, message: 'Goal is required' },
                      min: { value: 0, message: 'Goal must be >= 0' },
                      validate: (v) => v === '' || !Number.isNaN(Number(v)) || 'Goal must be a number'
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <MuiTextField
                        value={value || ''}
                        onChange={(event) => onChange(event.target.value)}
                        type="number"
                        fullWidth
                        variant="outlined"
                        placeholder={getGoalLabel(index)}
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{ step: 'any', min: 0 }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconButton 
                    onClick={() => {
                      const kpi = watch(`contract.contract_kpis.${index}`);
                      if (kpi?.id) {
                        setValue(`contract.contract_kpis.${index}._destroy`, true, { shouldDirty: true });
                      } else {
                        remove(index);
                      }
                    }}
                    aria-label={`Delete KPI ${index + 1}`}
                    color="error"
                  >
                    <DeleteOutlined />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
        </Stack>

        <Button 
          onClick={handleAddKpi} 
          aria-label="Add New KPI"
          size="small"
          variant="outlined"
          disabled={isLoading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {contractFormText.kpiSection.addButton}
        </Button>
      </Stack>
    </Paper>
  );
}

function DuplicateContractForm({ contract, onClose }: DuplicateContractFormProps) {
  const { id: contractId } = contract;
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const [isShowDetails, setShowDetails] = useState(false);
  const [duplicateAllLineItems, setDuplicateAllLineItems] = useState(true);
  const [duplicateAllKPIs, setDuplicateAllKPIs] = useState(false);
  const { constants } = useConstants();
  const STAGE = get(constants, 'RAILS.STAGE');
  const defaultValues = {
    id: null,
    number_of_copies: 1,
    contract: {
      name: null,
      start_date: null,
      end_date: null,
      advisory_budget: null,
      bonus: null,
      io_path: null,
      contract_kpis: [],
    },
    insertion_orders: null,
  };

  const form = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const { control, setValue, watch, handleSubmit, reset, getValues } = form;
  const { fields: insertionOrders } = useFieldArray({
    control,
    name: 'insertion_orders',
  });

  const { data } = useQuery({
    queryKey: ['copyLineItem', contractId],
    queryFn: () => copy(apiHost, contractId),
    select: mapContractToForm,
  });

  const ioPath = watch('contract.io_path');

  const watchedContractAdvisoryBudget = useWatch({ control, name: 'contract.advisory_budget' });
  const watchedContractBonus = useWatch({ control, name: 'contract.bonus' });
  const watchedContractStartDate = useWatch({ control, name: 'contract.start_date' });
  const watchedContractEndDate = useWatch({ control, name: 'contract.end_date' });
  const watchedInsertionOrdersDeep = useWatch({ control, name: 'insertion_orders' });

  // Calculates budget and bonus validation
  const budgetValidation = useMemo(() => {
    if (!watchedInsertionOrdersDeep || !watchedContractAdvisoryBudget) return { isValid: true };

    const selectedLineItems = watchedInsertionOrdersDeep.filter((io) => io?.clone?.copy);
    
    const totalLineItemBudget = selectedLineItems.reduce((sum, io) => {
      const budgetStr = String(io?.clone?.advisory_budget || 0).replace(/,/g, '');
      const budget = parseFloat(budgetStr) || 0;
      return sum + budget;
    }, 0);

    const totalLineItemBonus = selectedLineItems.reduce((sum, io) => {
      const bonusStr = String(io?.clone?.bonus || 0).replace(/,/g, '');
      const bonus = parseFloat(bonusStr) || 0;
      return sum + bonus;
    }, 0);

    const contractBudgetStr = String(watchedContractAdvisoryBudget || 0).replace(/,/g, '');
    const contractBudgetValue = parseFloat(contractBudgetStr) || 0;
    
    const contractBonusStr = String(watchedContractBonus || 0).replace(/,/g, '');
    const contractBonusValue = parseFloat(contractBonusStr) || 0;

    const budgetDifference = totalLineItemBudget - contractBudgetValue;
    const bonusDifference = totalLineItemBonus - contractBonusValue;

    return {
      isValid: budgetDifference <= 0 && bonusDifference <= 0,
      budgetDifference,
      bonusDifference,
      hasBudgetError: budgetDifference > 0,
      hasBonusError: bonusDifference > 0,
    };
  }, [watchedInsertionOrdersDeep, watchedContractAdvisoryBudget, watchedContractBonus]);

  // Calculate date validation
  const dateValidation = useMemo(() => {
    if (!watchedInsertionOrdersDeep || !watchedContractStartDate || !watchedContractEndDate) return { isValid: true, errors: [] };

    const selectedLineItems = watchedInsertionOrdersDeep.filter((io) => io?.clone?.copy);
    const errors: string[] = [];

    // Helper function to extract date string
    const getDateString = (dateValue: any): string => {
      if (!dateValue) return '';
      
      if (typeof dateValue === 'string' && dateValue.includes('T')) {
        return dateValue.split('T')[0];
      }
      
      return format(startOfDay(new Date(dateValue)), 'yyyy-MM-dd');
    };

    selectedLineItems.forEach((io, index) => {
      const lineItemStartDate = io?.clone?.start_date;
      const lineItemEndDate = io?.clone?.end_date;
      const lineItemName = io?.clone?.name || `Line Item ${index + 1}`;

      if (lineItemStartDate && lineItemEndDate) {
        const contractStartStr = getDateString(watchedContractStartDate);
        const contractEndStr = getDateString(watchedContractEndDate);
        const lineItemStartStr = getDateString(lineItemStartDate);
        const lineItemEndStr = getDateString(lineItemEndDate);

        // Checks if line item start date falls within contract dates
        if (lineItemStartStr < contractStartStr) {
          errors.push(`${lineItemName}: Start date is before contract start date`);
        }
        if (lineItemStartStr > contractEndStr) {
          errors.push(`${lineItemName}: Start date is after contract end date`);
        }
        
        // Checks if line item end date falls within contract dates
        if (lineItemEndStr < contractStartStr) {
          errors.push(`${lineItemName}: End date is before contract start date`);
        }
        if (lineItemEndStr > contractEndStr) {
          errors.push(`${lineItemName}: End date is after contract end date`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [watchedInsertionOrdersDeep, watchedContractStartDate, watchedContractEndDate]);

  const pasteMutation = useMutation({
    mutationFn: (payload) => paste(apiHost, contractId, payload),
    onSuccess: (response) => {
      posthog.capture('contract_duplicated', { source_contract_id: contractId });
      showSnackBar(response.status, 'success', { event: 'contract_duplicated', source_contract_id: contractId });
      if (onClose) {
        onClose();
      }
    },
  });

  useEffect(() => {
    if (data) {
      const hasKPIs = data.contract_kpis && data.contract_kpis.length > 0;
      const shouldDuplicateKPIs = hasKPIs; // Default to checked if original has KPIs
      setDuplicateAllKPIs(shouldDuplicateKPIs);
      
      reset({
        id: contractId,
        contract: {
          ...data,
          contract_kpis: shouldDuplicateKPIs ? data.contract_kpis : [],
        },
        insertion_orders: data.insertion_orders,
        number_of_copies: defaultValues.number_of_copies,
      });
    }
  }, [reset, data, contractId, defaultValues.number_of_copies]);

  // Copy KPIs when checkbox is checked/unchecked
  useEffect(() => {
    if (data) {
      if (duplicateAllKPIs && data.contract_kpis && data.contract_kpis.length > 0) {
        // Copy KPIs from original contract
        setValue('contract.contract_kpis', data.contract_kpis, { shouldDirty: true });
      } else if (!duplicateAllKPIs) {
        // When unchecked, ensure one empty row if currently empty
        const currentKpis = getValues('contract.contract_kpis') || [];
        // eslint-disable-next-line no-underscore-dangle
        const activeKpis = currentKpis.filter((kpi: any) => !kpi._destroy);
        if (activeKpis.length === 0) {
          setValue('contract.contract_kpis', [{
            kpiName: '',
            provider: '',
            goalValue: '',
            goalFormat: 'number',
            position: 0
          }], { shouldDirty: true });
        }
      }
    }
  }, [duplicateAllKPIs, data, setValue, getValues]);

  const toggleDuplicateAllKPIs = () => {
    setDuplicateAllKPIs(!duplicateAllKPIs);
  };

  // Watch KPIs for validation
  const watchedKPIs = watch('contract.contract_kpis') || [];
  // eslint-disable-next-line no-underscore-dangle
  const activeKPIs = watchedKPIs.filter((kpi: any) => !kpi._destroy && kpi.kpiName);
  const hasValidKPIs = activeKPIs.length > 0;

  // Combined form validation
  const isFormValid = budgetValidation.isValid && dateValidation.isValid && hasValidKPIs;

  const submitHandler = (payload) => {
    // Validate KPIs before submission
    const kpis = payload.contract?.contract_kpis || [];
    // eslint-disable-next-line no-underscore-dangle
    const activeKpisInPayload = kpis.filter((kpi: any) => !kpi._destroy && kpi.kpiName);
    
    if (activeKpisInPayload.length === 0) {
      showSnackBar('At least one KPI is required to duplicate this contract', 'error', { event: 'contract_duplicate_error', source_contract_id: contractId });
      return;
    }
    
    pasteMutation.mutate(mapFormToContract(payload));
  };

  const generateUploadKey = () => `${STAGE}/ios/tmp`;

  const toggleDuplicateAllLineItems = () => {
    const insertionOrdersRows = getValues('insertion_orders');
    insertionOrdersRows.forEach((insertionOrderRow, index) => {
      setValue(`insertion_orders.${index}.clone.copy`, !duplicateAllLineItems);
    });
    setDuplicateAllLineItems(!duplicateAllLineItems);
  };

  // Apply contract dates to line items
  const applyContractDatesToLineItems = () => {
    const start = getValues('contract.start_date');
    const end = getValues('contract.end_date');

    if (!start || !end) {
      showSnackBar('Set contract Start and End dates first.', 'error', { event: 'contract_apply_dates_error', source_contract_id: contractId });
      return;
    }

    const ios = getValues('insertion_orders') || [];
    ios.forEach((io: any, index: number) => {
      // Only touch IOs the user is duplicating
      if (io?.clone?.copy) {
        setValue(`insertion_orders.${index}.clone.start_date`, start, { shouldDirty: true, shouldValidate: true });
        setValue(`insertion_orders.${index}.clone.end_date`, end, { shouldDirty: true, shouldValidate: true });
      }
    });

    showSnackBar('Applied contract dates to selected line items.', 'success', { event: 'contract_dates_applied', source_contract_id: contractId });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Typography variant="h5" mx={2} fontWeight="bold" gutterBottom>
          Duplicate {contract.name}
        </Typography>
        <Stack alignItems="flex-start" spacing={2} padding={2}>
          <Paper sx={{ width: '100%', mb: 2 }}>
            <Box p={2}>
              <FormControlLabel
                label="Show Original Contract Details"
                control={<Switch onClick={() => setShowDetails(!isShowDetails)} />}
              />
              <Collapse in={isShowDetails} timeout="auto" unmountOnExit>
                <Box sx={{ height: '300px', overflowY: 'scroll' }}>
                  <Stack alignItems="flex-start" spacing={1} py={2}>
                    <TableContainer>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>{contract.name}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Bid Type</TableCell>
                            <TableCell>{contract.bid_type}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Contract CPM</TableCell>
                            <TableCell>{contract.contract_cpm}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Domain</TableCell>
                            <TableCell>{contract.domain}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Advisory Budget</TableCell>
                            <TableCell>{contract.advisory_budget}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Bonus</TableCell>
                            <TableCell>{contract.bonus}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Advisory Start Date</TableCell>
                            <TableCell>
                              {contract.start_date && format(new Date(contract.start_date), 'yyyy-MM-dd')}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Advisory End Date</TableCell>
                            <TableCell>
                              {contract.end_date && format(new Date(contract.end_date), 'yyyy-MM-dd')}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Categorization</TableCell>
                            <TableCell>{JSON.stringify(contract.categorization)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Dynamic Spend Pacing (Default)</TableCell>
                            <TableCell>{contract.dynamic_spend_pacing}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Pacing Strategies (Default)</TableCell>
                            <TableCell>{JSON.stringify(contract.pacing_strategies)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Cost Incurring Features</TableCell>
                            <TableCell>{contract.cost_incurring_features}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Alcohol Related Content</TableCell>
                            <TableCell>{contract.alcohol_related}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Allow Price Discovery</TableCell>
                            <TableCell>{contract.allow_price_discovery}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Placed Data</TableCell>
                            <TableCell>{contract.placed}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Has Search</TableCell>
                            <TableCell>{contract.hybrid}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </Paper>
          <TextField
            name="contract.name"
            label="New Contract Name"
            sx={{ width: '100%' }}
            rules={{ required: { value: true, message: 'Name is required' } }}
          />
          <Stack width="100%">
            <S3Upload
              name="contract.io_path"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              label="Drag/Drop Here Or Click To Upload IO"
              replaceLabel="Drag/Drop Here Or Click To Replace IO"
              s3InfoUrl={API_S3_UPLOAD_INFO_URL}
              generateKey={generateUploadKey}
              rules={{ required: { value: true, message: 'IO file is required' } }}
              preserveName
              sx={{ width: '100%' }}
            />
          </Stack>
          <Button
            variant="text"
            sx={{
              borderRadius: '8px',
              boxShadow: 'none',
              textTransform: 'none',
            }}
            disabled={!ioPath}
            onClick={() =>
              ioPath && window.open(`${apiHost}/admin/helpers/s3_get_resource/${encodeURIComponent(ioPath)}`, '_blank')
            }
          >
            View uploaded IO
          </Button>
          <Typography>Copies</Typography>
          <NumericTextField
            name="number_of_copies"
            label="Copies"
            allowNegative={false}
            decimalScale={0}
            isAllowed={(input) =>
              isBlank(input.floatValue) ||
              (input.floatValue >= defaultValues.number_of_copies && input.floatValue <= 10)
            }
            sx={{ width: '25%' }}
            rules={{ required: { value: true, message: 'Copies is required' } }}
          />
          <Typography>Flight Dates</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
              label="Start Date"
              name="contract.start_date"
              rules={{
                required: { value: true, message: 'Start date is required' },
              }}
            />
            <div>
              <Typography>To</Typography>
            </div>
            <DatePicker
              label="End Date"
              name="contract.end_date"
              rules={{
                required: { value: true, message: 'End date is required' },
              }}
            />
            <Button
              variant="outlined"
              onClick={applyContractDatesToLineItems}
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            >
              Apply dates to line items
            </Button>
          </Stack>
          {!dateValidation.isValid && (
            <Alert severity="error" sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Line item dates must fall within contract dates:
              </Typography>
              {dateValidation.errors.map((error) => (
                <Typography key={error} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Alert>
          )}
          <Typography>Budget</Typography>
          <Grid container>
            <Grid item xs={6}>
              <TextField
                name="contract.advisory_budget"
                label="Advisory Budget"
                sx={{ width: '80%' }}
                rules={{
                  required: { value: true, message: 'Advisory budget is required' },
                  onBlur: (e) =>
                    setValue('contract.advisory_budget', round(e?.target?.value), {
                      shouldDirty: true,
                      shouldValidate: true,
                    }),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="contract.bonus"
                label="Bonus"
                sx={{ width: '80%' }}
                rules={{
                  required: { value: true, message: 'Bonus is required' },
                  onBlur: (e) =>
                    setValue('contract.bonus', omitCents(e?.target?.value), {
                      shouldDirty: true,
                      shouldValidate: true,
                    }),
                }}
              />
            </Grid>
          </Grid>
          {!budgetValidation.isValid && (
            <Alert severity="error" sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight="bold">
                Budget exceeded:
              </Typography>
              {budgetValidation.hasBudgetError && (
                <Typography variant="body2">
                  • Advisory Budget is over by ${Math.abs(budgetValidation.budgetDifference).toFixed(2)}
                </Typography>
              )}
              {budgetValidation.hasBonusError && (
                <Typography variant="body2">
                  • Bonus is over by ${Math.abs(budgetValidation.bonusDifference).toFixed(2)}
                </Typography>
              )}
            </Alert>
          )}
          <Typography>KPI Duplication</Typography>
          <FormControlLabel
            label="Duplicate All KPIs"
            control={<Checkbox checked={duplicateAllKPIs} onChange={toggleDuplicateAllKPIs} />}
          />
          {!duplicateAllKPIs && (
            <DuplicateKpiSection
              control={control}
              watch={watch}
              setValue={setValue}
              apiHost={apiHost}
              hideSectionTitle
              hideKpiCount
            />
          )}
          {!hasValidKPIs && (
            <Typography variant="body2" color="error" sx={{ mt: -1 }}>
              At least one KPI is required to duplicate this contract
            </Typography>
          )}
          <Typography>Line Item Duplication</Typography>
          <FormControlLabel
            label="Duplicate All Line Items"
            control={<Checkbox checked={duplicateAllLineItems} onChange={toggleDuplicateAllLineItems} />}
          />
          {!isEmpty(insertionOrders) &&
            Object.values(insertionOrders).map((field, index) => (
              <DuplicateLineItemContainer
                key={field.id}
                lineItem={mapLineItemToForm(getValues(`insertion_orders.${index}.original`))}
                fieldNamePrefix={`insertion_orders.${index}.clone`}
              />
            ))}
          <FormControlLabel label="Duplicate to another Parent Item" disabled control={<Switch />} />
        </Stack>
        <FormEndOptions>
          <Stack direction="row" justifyContent="center" width="100%" spacing={2} pt={2}>
            <Button variant="outlined" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || pasteMutation.isLoading}
              aria-busy={pasteMutation.isLoading}
            >
              Duplicate
            </Button>
          </Stack>
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default DuplicateContractForm;